import { useLayoutEffect, useRef, type FocusEvent, type RefObject } from 'react'

/**
 * Focus handlers for a container whose children can leave on a refetch (a row
 * that turned inactive while hidden). When the focused element leaves with
 * them, focus goes to `fallback` instead of falling to the page.
 */
export function useFocusFallback(fallback: RefObject<HTMLElement | null>): {
  onFocusCapture: (event: FocusEvent<HTMLElement>) => void
  onBlurCapture: (event: FocusEvent<HTMLElement>) => void
} {
  const focused = useRef<Element | null>(null)
  // Every commit: React drops the removed element first, then runs this.
  useLayoutEffect(() => {
    const lost = focused.current
    if (!lost || lost.isConnected) return
    focused.current = null
    const active = document.activeElement
    if (active === null || active === document.body) fallback.current?.focus()
  })
  return {
    onFocusCapture: (event) => {
      focused.current = event.target
    },
    // React ignores the blur of an element it removes, so this is a real move away.
    onBlurCapture: (event) => {
      const next = event.relatedTarget
      if (!(next instanceof Node && event.currentTarget.contains(next)))
        focused.current = null
    },
  }
}
